import React from 'react';
import { Link } from 'react-router-dom';
import { ReactFlow, getViewportForBounds } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Trans, useTranslation } from 'react-i18next';
import AccountNode from './AccountNode';
import DirectionEdge from './DirectionEdge';
import { buttonClasses } from 'components/common/tailwindClassnames';
import { getRouteStatus } from 'components/routes/getRouteStatus';
import {
  FLOW_COLORS,
  FLOW_HOVER_COLORS,
  FlowDirectionKey,
} from './flowMapTypes';
import { verticalMapHeight } from './useFlowGraph';

const nodeTypes = { account: AccountNode };
const edgeTypes = { direction: DirectionEdge };

type RouteFlowMapProps = {
  nodes: any[];
  edges: any[];
  destinationCount: number;
  // Phone layout: nodes stacked top to bottom, sized to the screen.
  vertical?: boolean;
  routeStatus?: string;
  onSelectAccount: (integrationId: string) => void;
  onSelectFlow: (destinationId: string, direction: FlowDirectionKey) => void;
  onAddDestination: () => void;
  // Mirrors the header's Route Enabled switch — a disabled Route moves
  // no data, so the whole graph is muted and the state is called out in words.
  routeEnabled?: boolean;
  // Pulses the "+ Add destination" button — set right after creating a new
  // Route so the user's next step is obvious.
  highlightAddDestination?: boolean;
  // Re-enables a disabled Route from the banner, the only place that is
  // reachable when the map has no outgoing arrow to carry the toggle.
  onEnableRoute?: () => void;
  // Some account on the map still has required configuration missing — the
  // same answer the "!" badges are drawn from, so banner and badges agree.
  needsSetup?: boolean;
  // Where the unhealthy banner's "Logs tab" link goes — the banner tells the
  // user to check the Logs tab, so it should also take them there.
  logsPath?: string;
  // Carries the plain-click navigation instead of the Link's own, so the
  // owner can ask about unsaved panel edits first (guardedNavigate). Modifier
  // clicks keep the Link's default new-tab behavior — nothing unmounts, so
  // there is nothing to guard.
  onNavigateToLogs?: (path: string) => void;
};

// Same framing fitView would aim for: 15% padding, never zoomed past natural size.
const FIT_PADDING = 0.15,
  MIN_ZOOM = 0.1,
  MAX_ZOOM = 1;

// The attention strip under the header: full width, red outline, no fill.
const BANNER =
  'border border-ui-status-error-destructive px-3 md:px-5 py-2 text-sm text-ui-text-primary-text';

const LEGEND = [
  { label: 'Enabled', className: 'border-t-2 border-green-1 w-6' },
  {
    label: 'Disabled',
    className: 'border-t-2 border-dashed border-unlisted-gray-lines w-6',
  },
  {
    label: 'Not available',
    className: 'border-t-2 border-dashed border-unlisted-gray-lines w-6',
  },
  // Same amber "!" badge as the node/arrow indicators.
  {
    label: 'Needs setup',
    className:
      'w-4 h-4 rounded-full bg-ui-status-bg-error-bg text-ui-status-error-destructive border border-ui-status-error-destructive text-[10px] font-bold flex items-center justify-center',
    glyph: '!',
  },
];

const RouteFlowMap = ({
  nodes,
  edges,
  destinationCount,
  vertical = false,
  routeStatus,
  onSelectAccount,
  onSelectFlow,
  onAddDestination,
  highlightAddDestination,
  onEnableRoute,
  routeEnabled = true,
  needsSetup,
  logsPath,
  onNavigateToLogs,
}: RouteFlowMapProps) => {
  const { t } = useTranslation();
  const mapHeight = vertical
    ? verticalMapHeight(destinationCount)
    : Math.max(280, destinationCount * 132 + 80);
  // The status pill describes data flow, so it only means anything while the
  // Route is enabled — otherwise the disabled chip says it all.
  const status =
    routeStatus && routeEnabled
      ? getRouteStatus(routeStatus, true, t)
      : undefined;

  // The pulse stops on its own or as soon as the user clicks the button.
  const [highlight, setHighlight] = React.useState(!!highlightAddDestination);
  React.useEffect(() => {
    if (!highlightAddDestination) return;
    setHighlight(true);
    const timer = setTimeout(() => setHighlight(false), 8000);
    return () => clearTimeout(timer);
  }, [highlightAddDestination]);

  const [rfInstance, setRfInstance] = React.useState<any>(null);
  const canvasRef = React.useRef<HTMLDivElement | null>(null);
  // The layout is part of the key: the same ids sit at new positions when the
  // map flips between the horizontal and the phone layout.
  const nodeKey = `${vertical ? 'v' : 'h'}:${nodes.map((n) => n.id).join(',')}`;

  // The viewport is computed here, never by React Flow's fitView. fitView only
  // counts nodes that its ResizeObserver has MEASURED (getFitViewNodes filters on
  // n.measured.width) — a node's declared width/height makes it render but never
  // reaches the fit bounds. So any fit racing a measurement frames just the
  // already-measured subset: adding a destination framed the new node alone with
  // the provider shoved off-canvas, and removing the last one framed nothing.
  // Every node's position and size is declared in buildGraph, so the true bounds
  // are known synchronously — setViewport with our own math has nothing to race.
  // It also only sets a CSS transform, which cannot re-trigger the resize
  // observer the way fitView's layout nudges did.
  const frameView = React.useCallback(() => {
    const el = canvasRef.current;
    if (!rfInstance || !el || nodes.length === 0) return;
    const width = el.clientWidth,
      height = el.clientHeight;
    if (!width || !height) return;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const n of nodes) {
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxX = Math.max(maxX, n.position.x + (n.width ?? 0));
      maxY = Math.max(maxY, n.position.y + (n.height ?? 0));
    }
    rfInstance.setViewport(
      getViewportForBounds(
        { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
        width,
        height,
        MIN_ZOOM,
        MAX_ZOOM,
        FIT_PADDING,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfInstance, nodeKey]);

  // Reframe when the graph or the canvas height changes, and whenever the canvas
  // is resized (side drawer opening/closing, window resize). A zero-size canvas
  // is skipped — on a client-side navigation layout may not have happened yet —
  // and the observer delivers the real size as soon as it exists. The debounce
  // collapses a size that bounces during a drawer animation into one frame.
  React.useEffect(() => {
    const raf = requestAnimationFrame(frameView);
    const el = canvasRef.current;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(() => {
            clearTimeout(timer);
            timer = setTimeout(frameView, 150);
          });
    if (el) observer?.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      observer?.disconnect();
    };
  }, [frameView, mapHeight]);
  // Arrow clicks come from the edge's own label/hit-area handler, so the
  // callback rides along in edge data. onSelectFlow is an inline arrow that
  // closes over the panel's dirty state, so it must not be captured in the
  // memo — a stale copy would skip the "discard unsaved changes?" prompt.
  // A ref keeps the handler current without rebuilding the edges every render.
  const onSelectFlowRef = React.useRef(onSelectFlow);
  onSelectFlowRef.current = onSelectFlow;
  const selectFlow = React.useCallback(
    (destinationId: string, direction: FlowDirectionKey) =>
      onSelectFlowRef.current(destinationId, direction),
    [],
  );
  const edgesWithHandler = React.useMemo(
    () =>
      edges.map((e) => ({ ...e, data: { ...e.data, onSelect: selectFlow } })),
    [edges, selectFlow],
  );

  return (
    <div
      data-testid="RouteFlowMap-container"
      className="mb-6 rounded-lg border border-teal-t30 bg-white"
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-unlisted-gray-lines px-3 py-3 md:px-5">
        <span className="text-lg font-bold text-ui-text-primary-text">
          {t('Flow map')}
        </span>
        {status && <span className={status.style}>{status.text}</span>}
        <button
          data-testid="RouteFlowMap-add-destination-button"
          className={`${buttonClasses.headerActionGreen} ml-auto whitespace-nowrap ${
            highlight ? 'animate-pulse ring-2 ring-green-1 ring-offset-2' : ''
          }`}
          onClick={() => {
            setHighlight(false);
            onAddDestination();
          }}
        >
          {t('Add destination')}
        </button>
      </div>
      {/* One callout for whatever is keeping data from flowing: red outline,
                no fill. */}
      {!routeEnabled ? (
        <div className={`${BANNER} flex flex-wrap items-center gap-3`}>
          <span>
            {t(
              'This Route is disabled, so no data is moving along these flows.',
            )}
          </span>
          {/* The per-flow toggle lives on an outgoing arrow, and a Route
                        with no destinations has none — without this the state is a
                        dead end. */}
          {onEnableRoute && (
            <button
              type="button"
              className={`${buttonClasses.primary} ml-auto whitespace-nowrap`}
              onClick={() => onEnableRoute()}
            >
              {t('Enable')}
            </button>
          )}
        </div>
      ) : destinationCount === 0 ? (
        <div className={BANNER}>
          {t(
            'This Route has no destination yet, so data has nowhere to go. Use the “Add destination” button above to connect one.',
          )}
        </div>
      ) : needsSetup ? (
        <div className={BANNER}>
          {t(
            "This Route isn't fully set up yet. Look for the exclamation marks on the map.",
          )}
        </div>
      ) : routeStatus === 'unhealthy' ? (
        <div className={BANNER}>
          {/* "Logs tab" is the link itself — the sentence already says
                        where to go, so it shouldn't need a second call to action. */}
          {logsPath ? (
            <Trans
              i18nKey="unhealthyRouteBanner"
              components={{
                logsLink: (
                  <Link
                    to={logsPath}
                    className={buttonClasses.inlineLink}
                    onClick={(e) => {
                      if (
                        !onNavigateToLogs ||
                        e.metaKey ||
                        e.ctrlKey ||
                        e.shiftKey
                      )
                        return;
                      e.preventDefault();
                      onNavigateToLogs(logsPath);
                    }}
                  />
                ),
              }}
            />
          ) : (
            t('This Route needs attention. Check the Logs tab for any errors.')
          )}
        </div>
      ) : null}
      <div
        className={`overflow-x-auto ${routeEnabled ? '' : 'opacity-60 grayscale'}`}
      >
        <div
          ref={canvasRef}
          style={{ height: mapHeight, minWidth: vertical ? undefined : 880 }}
        >
          {/* arrowheads, one per state and per hover shade (SVG markers can't be Tailwind-coloured) */}
          <svg style={{ height: 0, width: 0, position: 'absolute' }}>
            <defs>
              {[
                ['', FLOW_COLORS],
                ['-hover', FLOW_HOVER_COLORS],
              ].flatMap(([suffix, palette]: any) =>
                Object.entries(palette).map(([state, color]) => (
                  <marker
                    key={`${state}${suffix}`}
                    id={`flow-arrow-${state}${suffix}`}
                    markerWidth="8"
                    markerHeight="8"
                    refX="6"
                    refY="3"
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L6,3 L0,6 z" fill={color as string} />
                  </marker>
                )),
              )}
            </defs>
          </svg>
          <ReactFlow
            nodes={nodes}
            edges={edgesWithHandler}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            edgesFocusable={false}
            panOnDrag={false}
            zoomOnScroll={false}
            zoomOnPinch={false}
            zoomOnDoubleClick={false}
            preventScrolling={false}
            proOptions={{ hideAttribution: true }}
            minZoom={MIN_ZOOM}
            onInit={setRfInstance}
            onNodeClick={(_: any, n: any) => onSelectAccount(n.id)}
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 border-t border-unlisted-gray-lines px-3 py-2 text-xs text-ui-text-primary-text md:px-5">
        {LEGEND.map((l: any) => (
          <span key={l.label} className="flex items-center gap-2">
            <span className={l.className}>{l.glyph}</span>
            {t(l.label as any)}
          </span>
        ))}
      </div>
    </div>
  );
};

export default RouteFlowMap;
